import { constants } from "buffer";
import { actionEmitter, EmitEvent } from "../config/ActionEmitter";
import { IApiResponse, IFooter, IHeader } from "../models/Interfaces";
import { BaseService } from "./BaseService";
import { v4 as uuid4 } from "uuid";

export class HeaderFooterService extends BaseService {

  public getSiteHeaders = async (enabledOnly: boolean = false) : Promise<Array<IHeader>> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/GetHeaders/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        return res.ReturnData;
      } else {
        if (res?.StatusCode !== 404) { // ignore 404 errors - this is from the API when no items configured
          this.EmitDataError(res, "Failed to retrieve site headers");
        }
      }
    }
    return [];
  }

  public saveSiteHeader = async (siteHeader: IHeader) : Promise<IHeader | null> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/AddUpdateHeader`;
      const res = await this._ajaxService.Post<IApiResponse>(url, siteHeader, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save site header");
      }
    }
    return null;
  }

  public deleteSiteHeader = async (siteHeaderId: string) : Promise<boolean> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/DeleteHeader/${this.siteKey}/${siteHeaderId}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove site header");
      }
    }
    return false;
  }

  public getSiteFooters = async (enabledonly: boolean = false) : Promise<Array<IFooter>> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/GetFooters/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Unable to retrieve footer information");
        }
      }
    }
    return [];
  }

  public saveSiteFooter = async (footer: IFooter) : Promise<IFooter | null> => {
    if (this.siteKey && footer) {
      const url: string = `/SiteAdmin/AddUpdateFooter`;
      const res = await this._ajaxService.Post<IApiResponse>(url, footer, true);

      if (res?.Success && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save footer information");
      }
    }
    return null;
  }

  public deleteSiteFooter = async (footerid: number) : Promise<boolean> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/DeleteFooter/${this.siteKey}/${footerid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove selected footer");
      }
    }
    return false;
  }

  public newSiteHeader = () : IHeader | null => {
    if (this.siteKey) {
      return {
        Id: uuid4(),
        MenuGroupId: "",
        SiteId: this.siteKey,
        HeaderContainerClass: "",
        HeaderImageContainerClass: "",
        HeaderImageUrl: "",
        HeaderText: "",
        HeaderTextContainerClass: "",
        Language: "",
        MenuAlign: "",
        MenuContainerClass: "",
        Name: "",
        ShowMenu: true,
        Menu: null,
        Site: null,
        SiteName: null
      }
    }
    return null;
  }

  public newSiteFooter = () : IFooter | null => {
    if (this.siteKey) {
      return {
        Id: 0,
        BackgroundColour: "",
        Column1BackgroundColour: "",
        Column1TextColour: "",
        Column2BackgroundColour: "",
        Column2TextColour: "",
        Column3BackgroundColour: "",
        Column3TextColour: "",
        ContainerClass: "",
        CopyrightBackgroundColour: "",
        CopyrightNotice: "",
        CopyrightTextColour: "",
        Language: "",
        ShowSocialMedia: true,
        SiteId: this.siteKey,
        SocialBackgroundColour: "",
        SocialMediaLinks: [],
        SocialTextColour: "",
        StyledColumn1: "",
        StyledColumn2: "",
        StyledColumn3: "",
        TextColour: "",
        Title: ""
      }
    }
    return null;
  }
}